import {
  Actions,
  Async,
  Dispatchers,
  TypedOmit,
} from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { useEffect, useState } from 'react';
import { ExtrasModel } from '../model/extras.model';
import { ContentVideo } from '../model/extras.domain';
import { CreateVideoInput } from 'generated/graphql';
import { v4 } from 'uuid';
import { useForm } from 'react-hook-form';
import { videoValidationSchema } from './video.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  defaultValues,
  toMixpanelContentType,
  toUpdateContentInput,
} from '../viewModel/extras.mappers';
import { toErrors } from '@client/new/pages/promotions/view-model/newDiscount.mappers';
import { mixTrack } from '@services/mixpanel';
import toast from 'react-hot-toast';
import { ApolloError } from '@apollo/client';
import { useKeepScrollPosition } from '@client/new/hooks/useKeepScrollPosition';
import {
  createFormRegister,
  createOnChangeHandler,
  ExtendedFormRegister,
  getFormMode,
} from '../viewModel/formHelpers';

export type VideoParams = {
  uuid: string;
  thumbnailTimestampMs?: number;
  thumbnailCustomURL?: string;
  title?: string;
};

type VideoActionMap = {
  PrepareCreate: { parentId: string };
  Edit: ContentVideo['value'];
  VideoSelected: VideoParams;
  Cancel: null;
  PrepareReupload: null;
  PrepareThumbnail: null;
  SelectThumbnail: TypedOmit<VideoParams, 'uuid'>;
};

export type VideoVMAction = Actions<VideoActionMap>;

type VideoViewModelParams = {
  model: ExtrasModel;
};

type Mode = 'none' | 'create' | 'reuploading' | 'edit' | 'thumbnail-edit';

export type VMState = {
  mode: Mode;
  parentId: string;
  content: ContentVideo['value'];
};

export type VideoViewModel = Dispatchers<VideoActionMap> &
  VMState & {
    register: ExtendedFormRegister<ContentVideo['value']>;
    errors: any;
    values: ContentVideo['value'];
    control: any;
    onChange: (name: keyof ContentVideo['value'], value: any) => void;
  };

export const useVideoViewModel = (
  p: VideoViewModelParams,
): Async<VideoViewModel> => {
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const [state, setState] = useState<VMState>({
    mode: 'none',
    parentId: '',
    content: defaultValues['ContentVideo'],
  });

  useKeepScrollPosition(state.mode, 'none');

  const form = useForm({
    reValidateMode: 'onBlur',
    defaultValues: defaultValues['ContentVideo'],
    resolver: zodResolver(videoValidationSchema),
  });

  useEffect(() => {
    if (state.content && Object.keys(state.content).length > 0)
      form.reset({ ...state.content });
  }, [form, state.content, state.parentId]);

  const dispatchers = getDispatchers<VideoActionMap>(
    _dispatch(setState, p.model),
  );

  const values = form.watch();

  const onSubmit = (value: ContentVideo['value']) =>
    update(p.model, value).then((content) =>
      setState((p) => ({
        ...p,
        content,
      })),
    );

  const onError = () => {
    setShowErrors(true);
    toast.error('Please fix the errors in the form.');
  };

  const register = createFormRegister({
    form,
    onSubmit,
    onError,
    showErrors,
    mode: getFormMode(state.mode),
  });

  const onChange = createOnChangeHandler({
    form,
    onSubmit,
    onError,
    mode: getFormMode(state.mode),
  });

  return {
    type: 'fetched',
    value: {
      ...state,
      ...dispatchers,
      register,
      onChange,
      values,
      control: form.control,
      errors: showErrors ? toErrors(form.formState.errors) : null,
    },
  };
};

const _dispatch =
  (
    setState: React.Dispatch<React.SetStateAction<VMState>>,
    model: ExtrasModel,
  ) =>
  (a: VideoVMAction) => {
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'PrepareCreate': {
        mixTrack({
          type: 'Video Upload',
        });
        return setState(() => ({
          mode: 'create',
          parentId: a.payload.parentId,
          content: defaultValues['ContentVideo'],
        }));
      }
      case 'Edit':
        return setState((p) => ({
          ...p,
          mode: 'edit',
          content: a.payload,
        }));
      case 'PrepareReupload':
        return setState((p) => ({
          ...p,
          mode: 'reuploading',
        }));
      case 'PrepareThumbnail': {
        return setState((p) => ({
          ...p,
          mode: 'thumbnail-edit',
        }));
      }
      case 'SelectThumbnail':
        return setState((p) => {
          update(model, {
            ...p.content,
            thumbnailTimestampMs: a.payload.thumbnailTimestampMs || 0,
            thumbnailCustomURL: a.payload.thumbnailCustomURL,
          } as ContentVideo['value']).then((content) => {
            setState((p) => ({
              ...p,
              content,
              mode: 'edit',
            }));
          });
          return p;
        });

      case 'Cancel': {
        return setState((p) => {
          if (p.mode === 'reuploading' || p.mode === 'thumbnail-edit')
            return {
              ...p,
              mode: 'edit',
            };
          return {
            ...p,
            mode: 'none',
            content: defaultValues['ContentVideo'],
          };
        });
      }
      case 'VideoSelected': {
        setState((p) => {
          if (p.mode === 'reuploading') {
            onReupload(setState, model, {
              uuid: a.payload.uuid,
              thumbnailTimestampMs: a.payload.thumbnailTimestampMs || 0,
              thumbnailCustomURL: a.payload.thumbnailCustomURL,
            });
            return p;
          }

          onCreate(setState, model, {
            uuid: a.payload.uuid,
            thumbnailTimestampMs: a.payload.thumbnailTimestampMs || 0,
            title: a.payload.title || 'N/A',
            thumbnailCustomURL: a.payload.thumbnailCustomURL,
          });

          return p;
        });
      }
    }
  };

export const onCreate = (
  setState: React.Dispatch<React.SetStateAction<VMState>>,
  model: ExtrasModel,
  params: VideoParams,
) => {
  setState((p) => {
    const id = v4();
    const value: CreateVideoInput = {
      id,
      allowComments: false,
      thumbnailTimestampMs: params.thumbnailTimestampMs || 0,
      title: params.title || 'N/A',
      parentId: p.parentId,
      isPublished: false,
      libraryVideoId: params.uuid,
      thumbnailCustomURL: params.thumbnailCustomURL,
    };

    model
      .create({
        input: { type: 'ContentVideo', value },
      })
      .then((res) => {
        if (!res.contentMap[id]) throw new Error('Failed to create');
        mixTrack({
          type: 'Video Upload complete',
        });
        setState((p) => ({
          ...p,
          mode: 'edit',
          content: res.contentMap[id].value as ContentVideo['value'],
        }));
      })
      .catch((e: ApolloError) => {
        if (e.message.includes('missing mux id')) {
          toast.error(
            "Looks like this video isn't compatible with Extras. Please upload again to use it.",
          );
        } else toast.error('Failed to create video content. Please try again.');
      });

    return p;
  });
};

export const onReupload = (
  setState: React.Dispatch<React.SetStateAction<VMState>>,
  model: ExtrasModel,
  params: VideoParams,
) => {
  setState((p) => {
    const nextValue: ContentVideo['value'] = {
      ...p.content,
      thumbnailTimestampMs: params.thumbnailTimestampMs || 0,
      libraryVideoId: params.uuid,
      thumbnailCustomURL:
        params.thumbnailCustomURL ?? p.content.thumbnailCustomURL,
      isProcessed: false,
    };

    update(model, nextValue)
      .then((content) => {
        setState((p) => ({
          ...p,
          content,
          mode: 'edit',
        }));
      })
      .catch(() => toast.error('Failed to update video. Please try again.'));

    return p;
  });
};

export const update = async (
  model: ExtrasModel,
  value: ContentVideo['value'],
): Promise<ContentVideo['value']> => {
  const res = await model.update(
    value.id,
    toUpdateContentInput({
      type: 'ContentVideo',
      value,
    }),
  );
  const c = res.contentMap[value.id].value as ContentVideo['value'];
  if (!c) throw new Error('Failed update');
  mixTrack({
    type: 'Extras Block Saved',
    payload: {
      title: c.title,
      type: toMixpanelContentType('ContentVideo'),
      'video time': c.durationMs / 1000 || 0,
      'video aspect ratio': 'n/a',
      'Comments on': c.allowComments || false,
    },
  });
  return c;
};
